import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Container, Alert } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import "leaflet/dist/leaflet.css";

const DNSHorrorStory = () => {
  const [blinkText, setBlinkText] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Create blinking effect for the warning text
  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkText((prev) => !prev);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Initialize Leaflet map
  useEffect(() => {
    // Only run this effect on the client side
    if (typeof window !== "undefined" && !mapLoaded) {
      // Dynamically import Leaflet to avoid SSR issues
      import("leaflet").then((L) => {
        // Make sure the map container exists
        const mapContainer = document.getElementById("map-container");
        if (!mapContainer) return;

        // Create map instance
        const map = L.map("map-container").setView([36.1699, -24.2], 5);

        // Add satellite tile layer
        L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        ).addTo(map);

        // Add a red circle to highlight the area
        // eslint-disable-next-line
        const circle = L.circle([36.1699, -24.2], {
          color: "red",
          fillColor: "#f03",
          fillOpacity: 0.3,
          radius: 50000,
        }).addTo(map);

        setMapLoaded(true);
      });
    }
  }, [mapLoaded]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      {/* Alert Banner */}
      <Alert
        severity="error"
        variant="filled"
        icon={<WarningIcon fontSize="large" />}
        sx={{
          mb: 3,
          fontSize: "1.5rem",
          backgroundColor: "#d32f2f",
          animation: "pulse 1.5s infinite",
          "@keyframes pulse": {
            "0%": { opacity: 0.8 },
            "50%": { opacity: 1 },
            "100%": { opacity: 0.8 },
          },
        }}
      >
        GOOGLE CYBER ATTACK DETECTED
      </Alert>

      {/* Main Content */}
      <Paper
        elevation={6}
        sx={{
          p: 4,
          backgroundColor: "#000",
          color: "#fff",
          border: "2px solid #d32f2f",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ color: "#d32f2f", fontWeight: "bold" }}
        >
          CRITICAL SECURITY ALERT
        </Typography>

        <Typography variant="h6" gutterBottom>
          Your connection to DNS server 8.8.8.8 has been compromised
        </Typography>

        <Box sx={{ my: 3, p: 2, backgroundColor: "#111", borderRadius: 1 }}>
          <Typography
            variant="body1"
            sx={{ fontFamily: "monospace", fontSize: "1.1rem" }}
          >
            IP Address: <span style={{ color: "#ff0000" }}>0.0.0.666</span>
            <br />
            Status: <span style={{ color: "#ff0000" }}>COMPROMISED</span>
            <br />
            Threat Level: <span style={{ color: "#ff0000" }}>CRITICAL</span>
          </Typography>
        </Box>

        <Typography
          variant="h5"
          sx={{
            my: 3,
            color: blinkText ? "#ff0000" : "transparent",
            fontWeight: "bold",
            textAlign: "center",
            textShadow: blinkText ? "0 0 10px rgba(255, 0, 0, 0.7)" : "none",
          }}
        >
          YOUR DATA IS NOW PART OF A BOTNET
        </Typography>

        {/* Leaflet Map instead of Google Maps */}
        <Box sx={{ my: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            <LocationOnIcon /> Your Connection Has Been Traced To:
          </Typography>
          <Box
            id="map-container"
            sx={{
              width: "100%",
              height: "400px",
              border: "1px solid #333",
              borderRadius: 1,
              mb: 1,
              overflow: "hidden",
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: "#aaa", fontStyle: "italic" }}
          >
            Location: Atlantis, Atlantic Ocean (36.1699° N, 24.2000° W)
          </Typography>
        </Box>

        {/* Fake Actions */}
        <Box
          sx={{
            my: 3,
            p: 2,
            backgroundColor: "#1a1a1a",
            borderRadius: 1,
            border: "1px dashed #d32f2f",
          }}
        >
          <Typography variant="body1" gutterBottom>
            Recommended actions:
          </Typography>
          <ul>
            <li>Format your hard drive immediately</li>
            <li>Destroy your router with a hammer</li>
            <li>Move to a remote cabin in the woods</li>
            <li>Learn to communicate via carrier pigeon</li>
          </ul>
        </Box>

        {/* Disclaimer */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 4,
            fontSize: "0.7rem",
            color: "#666",
            textAlign: "center",
          }}
        >
          This page is a parody and is not affiliated with Google or any DNS
          service. No actual security threat exists. The IP address 8.8.8.8 is
          Google's public DNS service. This page is created for entertainment
          purposes only.
        </Typography>
      </Paper>
    </Container>
  );
};

export default DNSHorrorStory;
